import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SchemaFieldProps } from '../types';
import FormField from '../../components/ui/FormField';
import Button from '../../components/ui/Button';
import NumericInput from '../../components/NumericInput';
import SearchableSelect from '../../components/SearchableSelect';
import type { SeasonOption } from '../../utils/seasonOptions';
import { formatPrice } from '../../utils/coursePricing';
import {
  DivisionScheme,
  DivisionSegmentKind,
  DivisionSegmentScheme,
  ResolvedSegment,
  coerceDivisionScheme,
  presetSplits,
  resolveDivisionSegments,
  segmentFraction,
  suggestedPrice,
} from './divisionScheme';
import './DivisionSchemeField.css';

/**
 * SchemaForm override for a membership's `divisionScheme` (season-division
 * pricing). A whole-season `fullPrice` + informational `premium`, then an
 * interactive multi-lane timeline: the always-offered full season plus zero or
 * more division lanes (halves / thirds / quarters / custom). Each lane's split
 * points are DRAGGABLE — including the preset lanes, so an admin can make an
 * otherwise-even split asymmetric (e.g. a longer first half). Per-segment prices
 * are edited below. When an active-season term is supplied, the timeline shows a
 * live month axis + concrete date windows (client mirror of the backend
 * resolver — the backend re-resolves authoritatively on enrollment).
 *
 * The field `value` IS the payload shape the membership create/update routes
 * accept for `divisionScheme` — no remapping needed by the caller.
 *
 * Extra props (beyond SchemaFieldProps) are injected by MembershipForm via a
 * wrapper (mirrors how MembershipTypeField receives narrowed enumValues):
 *  - `termStart` / `termEnd`: a FIXED season term for the live preview (the
 *    course season-bound modal). When provided, NO preview selector is shown.
 *  - `previewSeasons`: seasons the standalone editor may preview against. When
 *    no fixed term is given and this is non-empty, a "Preview against season"
 *    selector drives the timeline dates. Preview only — the stored
 *    `divisionScheme` (fractions) is unaffected by the chosen season.
 *  - `defaultPreviewSeasonId`: which `previewSeasons` entry to preselect.
 *  - `usedBySeasonCount`: how many seasons reuse this membership (shared-use
 *    warning when > 1).
 */
export interface DivisionSchemeFieldProps extends SchemaFieldProps {
  termStart?: string | null;
  termEnd?: string | null;
  previewSeasons?: SeasonOption[];
  defaultPreviewSeasonId?: number;
  usedBySeasonCount?: number;
}

const DAY = 86_400_000;

// Every preset kind is addable at most once (the resolver keys lanes by `kind`
// and `resolveChosenSegment` addresses a lane by its kind). A single `custom`
// lane supports arbitrary split points, covering the "custom split line" intent.
const ADDABLE_KINDS: DivisionSegmentKind[] = ['halves', 'thirds', 'quarters', 'custom'];

/** Even brand-hue ramp for segments (token-based, theme + scheme aware). */
function segFill(index: number, total: number): string {
  const t = total <= 1 ? 0 : index / (total - 1);
  const pct = (16 - t * 8).toFixed(1);
  return `color-mix(in oklab, var(--accent-primary) ${pct}%, var(--bg-medium))`;
}

const DivisionSchemeField: React.FC<DivisionSchemeFieldProps> = ({
  field,
  value,
  onChange,
  error,
  termStart,
  termEnd,
  previewSeasons,
  defaultPreviewSeasonId,
  usedBySeasonCount = 0,
}) => {
  const { t, i18n } = useTranslation();
  const uid = field.name || 'divisionScheme';
  const lang = i18n.language || 'en';

  const scheme = useMemo(() => coerceDivisionScheme(value), [value]);
  const emit = useCallback((next: DivisionScheme) => onChange(next), [onChange]);

  // --- preview-against-season selector ------------------------------------
  // A fixed term (course season-bound modal) wins and hides the selector.
  // Otherwise, when preview seasons are supplied, a selector drives the dates.
  const hasFixedTerm = termStart != null && termEnd != null;
  const previewList = useMemo(() => previewSeasons ?? [], [previewSeasons]);
  const showPreviewSelector = !hasFixedTerm && previewList.length > 0;

  const [previewSeasonId, setPreviewSeasonId] = useState<number | null>(() => {
    if (previewList.length === 0) return null;
    const def =
      defaultPreviewSeasonId != null
        ? previewList.find((s) => s._id === defaultPreviewSeasonId)
        : undefined;
    return (def ?? previewList[0])._id;
  });

  // Keep the selection valid as the (async-loaded) list arrives / changes.
  useEffect(() => {
    if (previewList.length === 0) {
      setPreviewSeasonId(null);
      return;
    }
    setPreviewSeasonId((cur) => {
      if (cur != null && previewList.some((s) => s._id === cur)) return cur;
      const def =
        defaultPreviewSeasonId != null
          ? previewList.find((s) => s._id === defaultPreviewSeasonId)
          : undefined;
      return (def ?? previewList[0])._id;
    });
  }, [previewList, defaultPreviewSeasonId]);

  const selectedPreview = showPreviewSelector
    ? previewList.find((s) => s._id === previewSeasonId) ?? previewList[0]
    : null;
  const effectiveTermStart = hasFixedTerm ? termStart : selectedPreview?.termStart ?? null;
  const effectiveTermEnd = hasFixedTerm ? termEnd : selectedPreview?.termEnd ?? null;

  const resolved = useMemo(
    () => resolveDivisionSegments(scheme, effectiveTermStart, effectiveTermEnd),
    [scheme, effectiveTermStart, effectiveTermEnd],
  );
  const span = resolved.span; // inclusive day-count, or null when no term
  const hasTerm = span != null;
  const denom = span ?? 100; // percentage units when no term

  const [dragKey, setDragKey] = useState<string | null>(null);

  // Per-segment override tracking (component-local, NOT persisted). A key
  // `${laneKind}:${segIdx}` marks a price the admin typed by hand; those are
  // kept verbatim across divider drags / premium changes, while every other
  // (auto) segment is recomputed from its fraction. Lane kinds are unique per
  // lane, so the kind is a stable lane key. Mirrors the approved mockup's
  // "auto" / "edited" badges.
  //
  // Seeded ONCE from the loaded scheme: any stored price that differs from its
  // computed auto suggestion is treated as a manual override, so an edited
  // membership's hand-tuned prices survive the first drag (and are badged
  // "edited") rather than being silently recomputed away.
  const [overrides, setOverrides] = useState<Set<string>>(() => {
    const init = new Set<string>();
    for (const lane of scheme.schemes) {
      const count = lane.splits.length + 1;
      for (let i = 0; i < count; i++) {
        const auto = suggestedPrice(scheme, segmentFraction(lane, i));
        if ((lane.prices[i] ?? auto) !== auto) init.add(`${lane.kind}:${i}`);
      }
    }
    return init;
  });
  const isOverridden = useCallback(
    (kind: DivisionSegmentKind, segIdx: number) => overrides.has(`${kind}:${segIdx}`),
    [overrides],
  );

  // --- formatting helpers -------------------------------------------------
  const fmtDate = useCallback(
    (d: Date | null) =>
      d ? d.toLocaleDateString(lang, { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '',
    [lang],
  );
  const fmtLongDate = useCallback(
    (d: Date | null) =>
      d
        ? d.toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
        : '',
    [lang],
  );
  const money = useCallback((n: number) => formatPrice(n, lang) ?? '', [lang]);

  const unitFor = useCallback(
    (kind: DivisionSegmentKind) => {
      switch (kind) {
        case 'halves':
          return t('memberships.unitHalf');
        case 'thirds':
          return t('memberships.unitThird');
        case 'quarters':
          return t('memberships.unitQuarter');
        default:
          return t('memberships.unitPart');
      }
    },
    [t],
  );
  const schemeTitle = useCallback(
    (kind: DivisionSegmentKind) => {
      switch (kind) {
        case 'halves':
          return t('memberships.schemeByHalves');
        case 'thirds':
          return t('memberships.schemeByThirds');
        case 'quarters':
          return t('memberships.schemeByQuarters');
        default:
          return t('memberships.schemeCustom');
      }
    },
    [t],
  );
  const segLabel = useCallback(
    (kind: DivisionSegmentKind, index: number) =>
      t('memberships.segmentLabel', { index: index + 1, unit: unitFor(kind) }),
    [t, unitFor],
  );

  // --- month ticks (shared axis) ------------------------------------------
  const monthTicks = useMemo(() => {
    if (!hasTerm || !resolved.termStart || !span) return [] as { left: number; label: string }[];
    const start = resolved.termStart;
    const ticks: { left: number; label: string }[] = [];
    let d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    // advance to the first month boundary at/after the term start
    if (d.getTime() < start.getTime()) {
      d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
    }
    const endTime = start.getTime() + (span - 1) * DAY;
    while (d.getTime() <= endTime) {
      const offset = Math.round((d.getTime() - start.getTime()) / DAY);
      ticks.push({
        left: (offset / span) * 100,
        label: d.toLocaleDateString(lang, { month: 'short', timeZone: 'UTC' }),
      });
      d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
    }
    // Thin the rendered labels so short month names never collide, regardless
    // of viewport width: keep a tick only once it clears a minimum horizontal
    // gap from the previously kept one. Long seasons then show every 2nd/3rd
    // month while short seasons still show every month. Purely presentational
    // (the labels are aria-hidden); the segment/divider math is untouched.
    const MIN_GAP_PCT = 9; // min % of track width between adjacent labels
    const kept: { left: number; label: string }[] = [];
    for (const tk of ticks) {
      if (kept.length === 0 || tk.left - kept[kept.length - 1].left >= MIN_GAP_PCT) {
        kept.push(tk);
      }
    }
    return kept;
  }, [hasTerm, resolved.termStart, span, lang]);

  // --- mutations ----------------------------------------------------------
  /**
   * Suggested prices for every segment of a lane against a given base scheme,
   * keeping any OVERRIDDEN (hand-typed) segment's stored value. Auto segments
   * follow `suggestedPrice(fullPrice × fraction × (1+premium))`.
   */
  const pricesFor = useCallback(
    (seg: DivisionSegmentScheme, base: DivisionScheme, overrideSet: Set<string>): number[] => {
      const count = seg.splits.length + 1;
      const out: number[] = [];
      for (let i = 0; i < count; i++) {
        const auto = suggestedPrice(base, segmentFraction(seg, i));
        out.push(overrideSet.has(`${seg.kind}:${i}`) ? seg.prices[i] ?? auto : auto);
      }
      return out;
    },
    [],
  );
  // All-auto prices (ignores overrides) — for a freshly (re)built lane.
  const suggestedPricesFor = useCallback(
    (seg: DivisionSegmentScheme): number[] => {
      const count = seg.splits.length + 1;
      const out: number[] = [];
      for (let i = 0; i < count; i++) out.push(suggestedPrice(scheme, segmentFraction(seg, i)));
      return out;
    },
    [scheme],
  );

  // Drop every override key belonging to a lane kind (lane reset to all-auto).
  const clearLaneOverrides = (kind: DivisionSegmentKind) =>
    setOverrides((prev) => {
      if (![...prev].some((k) => k.startsWith(`${kind}:`))) return prev;
      const next = new Set(prev);
      for (const k of next) if (k.startsWith(`${kind}:`)) next.delete(k);
      return next;
    });

  // Recompute every lane's auto (non-overridden) prices against a base scheme
  // (whose fullPrice/premium may have changed) and emit the whole scheme.
  const recomputeAllLanes = (base: DivisionScheme) =>
    emit({
      ...base,
      schemes: base.schemes.map((seg) => ({ ...seg, prices: pricesFor(seg, base, overrides) })),
    });

  const setFullPrice = (n: number) => recomputeAllLanes({ ...scheme, fullPrice: n });
  const setPremium = (pct: number) => {
    const clamped = Math.max(0, Math.min(25, pct)) / 100;
    recomputeAllLanes({ ...scheme, premium: clamped });
  };

  const addScheme = (kind: DivisionSegmentKind) => {
    if (scheme.schemes.some((s) => s.kind === kind)) return;
    clearLaneOverrides(kind); // fresh lane starts all-auto
    const seg: DivisionSegmentScheme = { kind, splits: presetSplits(kind), prices: [] };
    seg.prices = suggestedPricesFor(seg);
    emit({ ...scheme, schemes: [...scheme.schemes, seg] });
  };
  const removeScheme = (laneIdx: number) => {
    const lane = scheme.schemes[laneIdx];
    if (lane) clearLaneOverrides(lane.kind);
    emit({ ...scheme, schemes: scheme.schemes.filter((_, i) => i !== laneIdx) });
  };
  const updateLane = (laneIdx: number, next: DivisionSegmentScheme) =>
    emit({ ...scheme, schemes: scheme.schemes.map((s, i) => (i === laneIdx ? next : s)) });

  const setSegmentPrice = (laneIdx: number, segIdx: number, price: number) => {
    const lane = scheme.schemes[laneIdx];
    if (!lane) return;
    const prices = lane.prices.slice();
    prices[segIdx] = price;
    // Editing a segment marks it overridden — it now sticks through drags.
    setOverrides((prev) => new Set(prev).add(`${lane.kind}:${segIdx}`));
    updateLane(laneIdx, { ...lane, prices });
  };
  const applyAutoPrices = (laneIdx: number) => {
    const lane = scheme.schemes[laneIdx];
    if (!lane) return;
    clearLaneOverrides(lane.kind); // "auto price" resets the lane to all-auto
    updateLane(laneIdx, { ...lane, prices: suggestedPricesFor(lane) });
  };

  /**
   * Move split `splitIdx` of a lane to fraction `frac`. Snaps to whole days when
   * a term is known, clamps between neighbours with a minimum 1-day (or 2%) gap.
   * Prices are kept by index (segment count is unchanged), so a drag just
   * reshapes the windows — asymmetric splits are fully supported.
   */
  const setSplit = (laneIdx: number, frac: number, splitIdx: number) => {
    const lane = scheme.schemes[laneIdx];
    if (!lane) return;
    const splits = lane.splits.slice();
    let f = frac;
    if (span && span > 0) f = Math.round(f * span) / span;
    const gap = span && span > 0 ? 1 / span : 0.02;
    const lower = (splitIdx === 0 ? 0 : splits[splitIdx - 1]) + gap;
    const upper = (splitIdx === splits.length - 1 ? 1 : splits[splitIdx + 1]) - gap;
    f = Math.max(lower, Math.min(upper, f));
    splits[splitIdx] = Number(f.toFixed(4));
    // Reshaping the windows changes each segment's fraction: recompute every
    // NON-overridden segment's auto price from its new fraction; overridden
    // (hand-typed) segments keep their value.
    const next: DivisionSegmentScheme = { ...lane, splits };
    next.prices = pricesFor(next, scheme, overrides);
    updateLane(laneIdx, next);
  };

  const beginDrag = (laneIdx: number, splitIdx: number) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const track = (e.currentTarget as HTMLElement).parentElement;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    setDragKey(`${laneIdx}:${splitIdx}`);
    const move = (ev: PointerEvent) => {
      if (rect.width <= 0) return;
      setSplit(laneIdx, (ev.clientX - rect.left) / rect.width, splitIdx);
    };
    const up = () => {
      setDragKey(null);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const nudge = (laneIdx: number, splitIdx: number, dir: -1 | 1) => {
    const lane = scheme.schemes[laneIdx];
    if (!lane) return;
    const step = span && span > 0 ? 1 / span : 0.01;
    setSplit(laneIdx, (lane.splits[splitIdx] ?? 0) + dir * step, splitIdx);
  };

  const addCustomSplit = (laneIdx: number) => {
    const lane = scheme.schemes[laneIdx];
    if (!lane) return;
    const bounds = [0, ...lane.splits, 1];
    let best = 0;
    let at = 0;
    for (let i = 0; i < bounds.length - 1; i++) {
      const w = (bounds[i + 1] ?? 1) - (bounds[i] ?? 0);
      if (w > best) {
        best = w;
        at = i;
      }
    }
    const mid = Number((((bounds[at] ?? 0) + (bounds[at + 1] ?? 1)) / 2).toFixed(4));
    const splits = [...lane.splits, mid].sort((a, b) => a - b);
    // Changing the segment count resets the lane to all-auto (indices shift).
    clearLaneOverrides(lane.kind);
    const next: DivisionSegmentScheme = { ...lane, splits, prices: [] };
    next.prices = suggestedPricesFor(next);
    updateLane(laneIdx, next);
  };
  const removeLastSplit = (laneIdx: number) => {
    const lane = scheme.schemes[laneIdx];
    if (!lane || lane.splits.length <= 1) return;
    const splits = lane.splits.slice(0, -1);
    clearLaneOverrides(lane.kind);
    const next: DivisionSegmentScheme = { ...lane, splits, prices: [] };
    next.prices = suggestedPricesFor(next);
    updateLane(laneIdx, next);
  };

  // --- timeline rendering -------------------------------------------------
  const renderTrackInner = (
    segments: ResolvedSegment[],
    total: number,
    kind: DivisionSegmentKind | 'full',
    laneIdx: number | null,
    splits: number[],
  ) => (
    <>
      {monthTicks.map((tk, i) => (
        <span key={`g${i}`} className="ds-tl__grid" style={{ left: `${tk.left}%` }} aria-hidden="true" />
      ))}
      {segments.map((s, i) => (
        <div
          key={`s${i}`}
          className="ds-tl__seg"
          style={{ left: `${(s.startOffset / denom) * 100}%`, width: `${(s.days / denom) * 100}%`, background: segFill(i, total) }}
        >
          <span className="ds-tl__seg-name">
            {kind === 'full' ? t('memberships.wholeSeason') : segLabel(kind as DivisionSegmentKind, i)}
          </span>
          {hasTerm && (
            <span className="ds-tl__seg-meta">
              {fmtDate(s.startDate)} – {fmtDate(s.endDate)}
            </span>
          )}
          <span className="ds-tl__seg-price">{money(s.price)}</span>
        </div>
      ))}
      {laneIdx != null &&
        splits.map((frac, splitIdx) => {
          const key = `${laneIdx}:${splitIdx}`;
          const tip = hasTerm && resolved.termStart
            ? fmtDate(new Date(resolved.termStart.getTime() + Math.round(frac * (span ?? 0)) * DAY))
            : `${Math.round(frac * 100)}%`;
          return (
            <div
              key={`d${splitIdx}`}
              className={`ds-tl__divider${dragKey === key ? ' is-drag' : ''}`}
              style={{ left: `${frac * 100}%` }}
              role="slider"
              tabIndex={0}
              aria-label={t('memberships.splitPoint')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(frac * 100)}
              aria-valuetext={tip}
              onPointerDown={beginDrag(laneIdx, splitIdx)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  nudge(laneIdx, splitIdx, -1);
                } else if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  nudge(laneIdx, splitIdx, 1);
                }
              }}
            >
              <span className="ds-tl__tip">{tip}</span>
              <span className="ds-tl__bar" aria-hidden="true" />
              <span className="ds-tl__grip" aria-hidden="true">
                <span />
                <span />
              </span>
            </div>
          );
        })}
    </>
  );

  return (
    <div className="ds-field">
      {/* Whole-season price + premium */}
      <div className="ds-head">
        <FormField label={t('memberships.fullPrice')} htmlFor={`sf-${uid}-fullPrice`} required={field.required} error={error}>
          <NumericInput
            id={`sf-${uid}-fullPrice`}
            min={0}
            step={10}
            value={String(scheme.fullPrice)}
            onChange={(e) => setFullPrice(parseFloat(e.target.value) || 0)}
          />
        </FormField>
        <FormField label={t('memberships.premium')} htmlFor={`sf-${uid}-premium`} labelHelpText={t('memberships.premiumHelp')}>
          <NumericInput
            id={`sf-${uid}-premium`}
            min={0}
            max={25}
            step={1}
            value={String(Math.round((scheme.premium || 0) * 100))}
            onChange={(e) => setPremium(parseFloat(e.target.value) || 0)}
          />
        </FormField>
      </div>

      {usedBySeasonCount > 1 && (
        <div className="ds-warning" role="alert">
          {t('memberships.sharedUseWarning', { count: usedBySeasonCount })}
        </div>
      )}

      {showPreviewSelector && (
        <FormField label={t('memberships.previewAgainstSeason')} htmlFor={`sf-${uid}-preview`}>
          <SearchableSelect
            id={`sf-${uid}-preview`}
            options={previewList.map((s) => ({ _id: s._id, name: s.name }))}
            value={selectedPreview?._id ?? null}
            onChange={(v) => setPreviewSeasonId(v == null ? null : Number(v))}
            allowClear={false}
          />
        </FormField>
      )}

      <div className="ds-term">
        {hasTerm ? (
          <span>
            {t('memberships.previewTerm', {
              start: fmtLongDate(resolved.full.startDate),
              end: fmtLongDate(resolved.full.endDate),
              days: span,
            })}
          </span>
        ) : (
          <span className="ds-term__note">{t('memberships.previewNoTerm')}</span>
        )}
      </div>

      {/* Interactive multi-lane timeline */}
      <div className="ds-tl">
        {hasTerm && (
          <>
            <div className="ds-tl__corner" aria-hidden="true" />
            <div className="ds-tl__months" aria-hidden="true">
              {monthTicks.map((tk, i) => (
                <span key={i} className="ds-tl__month" style={{ left: `${tk.left}%` }}>
                  {tk.label}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Full season lane (always offered) */}
        <div className="ds-tl__label">
          <span className="ds-tl__label-name">{t('memberships.wholeSeason')}</span>
          <span className="ds-tl__label-sub">{t('memberships.alwaysOffered')}</span>
        </div>
        <div className="ds-tl__track ds-tl__track--full">
          {renderTrackInner([resolved.full], 1, 'full', null, [])}
        </div>

        {/* Scheme lanes */}
        {scheme.schemes.map((seg, laneIdx) => {
          const laneSegments = resolved.schemes[seg.kind] ?? [];
          const total = seg.splits.length + 1;
          return (
            <React.Fragment key={`${seg.kind}-${laneIdx}`}>
              <div className="ds-tl__label">
                <span className="ds-tl__label-name">{schemeTitle(seg.kind)}</span>
                <span className="ds-tl__label-sub">{t('memberships.optionsCount', { count: total })}</span>
              </div>
              <div className="ds-tl__track">
                {renderTrackInner(laneSegments, total, seg.kind, laneIdx, seg.splits)}
              </div>
            </React.Fragment>
          );
        })}

        <p className="ds-tl__hint">{t('memberships.dragHint')}</p>
      </div>

      {/* Add-lane controls */}
      <div className="ds-add">
        <span className="ds-add__label">{t('memberships.addScheme')}</span>
        {ADDABLE_KINDS.map((kind) => (
          <Button
            key={kind}
            type="button"
            variant="ghost"
            size="sm"
            disabled={scheme.schemes.some((s) => s.kind === kind)}
            onClick={() => addScheme(kind)}
          >
            + {schemeTitle(kind)}
          </Button>
        ))}
      </div>

      {/* Per-lane pricing editors */}
      {scheme.schemes.map((seg, laneIdx) => {
        const total = seg.splits.length + 1;
        const isCustom = seg.kind === 'custom';
        const laneResolved = resolved.schemes[seg.kind] ?? [];
        return (
          <div className="ds-lane" key={`price-${seg.kind}-${laneIdx}`}>
            <div className="ds-lane__head">
              <div>
                <div className="ds-lane__title">{schemeTitle(seg.kind)}</div>
                <div className="ds-lane__sub">{t('memberships.optionsCount', { count: total })}</div>
              </div>
              <div className="ds-lane__actions">
                {isCustom && (
                  <>
                    <Button type="button" variant="ghost" size="sm" onClick={() => addCustomSplit(laneIdx)}>
                      {t('memberships.addSplit')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLastSplit(laneIdx)}
                      disabled={seg.splits.length <= 1}
                    >
                      {t('memberships.removeSplit')}
                    </Button>
                  </>
                )}
                <Button type="button" variant="ghost" size="sm" onClick={() => applyAutoPrices(laneIdx)}>
                  {t('memberships.autoPrice')}
                </Button>
                <Button type="button" variant="danger" size="sm" onClick={() => removeScheme(laneIdx)}>
                  {t('memberships.removeScheme')}
                </Button>
              </div>
            </div>

            <div className="ds-price-wrap">
              <table className="ds-price">
                <thead>
                  <tr>
                    <th>{t('memberships.colOption')}</th>
                    <th>{t('memberships.colDates')}</th>
                    <th>{t('memberships.colLength')}</th>
                    <th>{t('memberships.colPrice')}</th>
                    <th>{t('memberships.colPerMonth')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: total }).map((_, segIdx) => {
                    const rs = laneResolved[segIdx];
                    const price = seg.prices[segIdx] ?? 0;
                    const edited = isOverridden(seg.kind, segIdx);
                    const perMonth =
                      hasTerm && rs && rs.days > 0 ? price / (rs.days / 30.44) : null;
                    return (
                      <tr key={segIdx}>
                        <td className="ds-price__opt">
                          <span className="ds-price__dot" style={{ background: segFill(segIdx, total) }} />
                          <span className="ds-price__name">{segLabel(seg.kind, segIdx)}</span>
                        </td>
                        <td className="ds-price__dates">
                          {hasTerm && rs ? `${fmtDate(rs.startDate)} – ${fmtDate(rs.endDate)}` : '—'}
                        </td>
                        <td className="ds-price__len">
                          {hasTerm && rs ? t('memberships.daysCount', { count: rs.days }) : '—'}
                        </td>
                        <td className="ds-price__price">
                          <span className="ds-price__field">
                            <NumericInput
                              id={`sf-${uid}-l${laneIdx}-p${segIdx}`}
                              min={0}
                              step={10}
                              hideButtons
                              aria-label={t('memberships.segmentPrice')}
                              value={String(price)}
                              onChange={(e) => setSegmentPrice(laneIdx, segIdx, parseFloat(e.target.value) || 0)}
                            />
                            <span className="ds-price__cur">CZK</span>
                          </span>
                          <span className={`ds-price__badge${edited ? ' is-edited' : ''}`}>
                            {edited ? t('memberships.editedBadge') : t('memberships.autoBadge')}
                          </span>
                        </td>
                        <td className="ds-price__permo">
                          {perMonth != null ? t('memberships.perMonthValue', { price: money(Math.floor(perMonth)) }) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DivisionSchemeField;
